import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getDatabase, ref as dbRef, push } from 'firebase/database';
import { Attachment, ImageAttachment, FileAttachment } from '../../../../types/attachments';
import { formatDateOnlyForStorage, formatDateForStorage } from '../../../../Helpers/DateUtils';
import { getImageDimensions } from '../../../../Helpers/ImageUploadUtils';
import { recordActivity } from '../../../../api/activityApi';

export interface MaintenanceAttachmentUploadResult {
    success: boolean;
    attachment?: Attachment;
    error?: string;
}

// File size limit: 20MB
const MAX_FILE_SIZE = 20 * 1024 * 1024;

// Max attachments per activity
export const MAX_ATTACHMENTS_PER_ACTIVITY = 10;

/**
 * Magic byte signatures for file type validation.
 * Prevents MIME type spoofing attacks.
 */
const MAGIC_BYTES: Record<string, number[][]> = {
    // JPEG: starts with FF D8 FF
    'image/jpeg': [[0xFF, 0xD8, 0xFF]],
    // PNG: starts with 89 50 4E 47 0D 0A 1A 0A
    'image/png': [[0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A]],
    // GIF: starts with GIF87a or GIF89a
    'image/gif': [
        [0x47, 0x49, 0x46, 0x38, 0x37, 0x61], // GIF87a
        [0x47, 0x49, 0x46, 0x38, 0x39, 0x61]  // GIF89a
    ],
    // WebP: starts with RIFF....WEBP
    'image/webp': [[0x52, 0x49, 0x46, 0x46]], // Just check RIFF header
    // PDF: starts with %PDF
    'application/pdf': [[0x25, 0x50, 0x44, 0x46]]
};

/**
 * HEIC files are more complex - they use the ISO Base Media File Format.
 * Check for 'ftyp' box at offset 4 with various HEIC brand identifiers.
 */
const HEIC_BRANDS = ['heic', 'heix', 'mif1', 'msf1'];

/**
 * Read the first N bytes from a file as an array.
 */
const readFileBytes = async (file: File, numBytes: number): Promise<number[]> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const arrayBuffer = reader.result as ArrayBuffer;
            const bytes = new Uint8Array(arrayBuffer);
            resolve(Array.from(bytes));
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(file.slice(0, numBytes));
    });
};

/**
 * Check if the file bytes match any of the expected magic byte patterns.
 */
const matchesMagicBytes = (fileBytes: number[], patterns: number[][]): boolean => {
    return patterns.some(pattern => {
        if (fileBytes.length < pattern.length) return false;
        return pattern.every((byte, index) => fileBytes[index] === byte);
    });
};

/**
 * Check if a file is a valid HEIC/HEIF image by checking for ftyp box.
 */
const isValidHeicFile = (fileBytes: number[]): boolean => {
    // HEIC files have 'ftyp' at offset 4
    if (fileBytes.length < 12) return false;

    // Check for 'ftyp' at offset 4
    const ftyp = String.fromCharCode(fileBytes[4], fileBytes[5], fileBytes[6], fileBytes[7]);
    if (ftyp !== 'ftyp') return false;

    // Check for HEIC brand at offset 8
    const brand = String.fromCharCode(fileBytes[8], fileBytes[9], fileBytes[10], fileBytes[11]);
    return HEIC_BRANDS.includes(brand.toLowerCase());
};

/**
 * Validate file magic bytes to prevent MIME type spoofing.
 * Returns true if the file's actual content matches its declared MIME type.
 */
export const validateFileMagicBytes = async (file: File): Promise<{ valid: boolean; error?: string }> => {
    try {
        const fileBytes = await readFileBytes(file, 16);
        const mimeType = file.type.toLowerCase();

        // Check known magic byte patterns
        if (MAGIC_BYTES[mimeType]) {
            if (!matchesMagicBytes(fileBytes, MAGIC_BYTES[mimeType])) {
                return {
                    valid: false,
                    error: 'Filinnehållet matchar inte filtypen. Filen kan vara skadad eller manipulerad.'
                };
            }
            return { valid: true };
        }

        // Special handling for HEIC/HEIF
        if (mimeType === 'image/heic' || mimeType === 'image/heif') {
            if (!isValidHeicFile(fileBytes)) {
                return {
                    valid: false,
                    error: 'Ogiltig HEIC-fil.'
                };
            }
            return { valid: true };
        }

        // For other image types, at least verify they start with image-like content
        if (mimeType.startsWith('image/')) {
            // Check for common image headers
            const isKnownImage = Object.values(MAGIC_BYTES)
                .filter((_, key) => Object.keys(MAGIC_BYTES)[key as any]?.startsWith('image/'))
                .some(patterns => matchesMagicBytes(fileBytes, patterns));

            if (!isKnownImage) {
                // Could be a valid but uncommon image format, allow with warning log
                console.warn(`Unknown image format for MIME type: ${mimeType}`);
            }
            return { valid: true };
        }

        // Allow other file types (future-proofing)
        return { valid: true };
    } catch (error) {
        console.error('Error validating file magic bytes:', error);
        return {
            valid: false,
            error: 'Kunde inte validera filen. Försök igen.'
        };
    }
};

/**
 * Check if a file is an image type
 */
export const isImageFile = (file: File): boolean => {
    return file.type.startsWith('image/');
};

/**
 * Validate file before upload (MIME type and size).
 * For security-critical validation, also call validateFileMagicBytes.
 */
export const validateFile = (file: File): { valid: boolean; error?: string } => {
    if (file.size > MAX_FILE_SIZE) {
        return {
            valid: false,
            error: `Filen är för stor. Max storlek är ${MAX_FILE_SIZE / (1024 * 1024)}MB.`
        };
    }

    // Allow images and PDFs
    const allowedTypes = ['image/', 'application/pdf'];
    const isAllowed = allowedTypes.some(type => file.type.startsWith(type));

    if (!isAllowed) {
        return {
            valid: false,
            error: 'Endast bilder och PDF-filer är tillåtna.'
        };
    }

    return { valid: true };
};

/**
 * Full file validation including magic bytes.
 * Use this for more secure validation.
 */
export const validateFileWithMagicBytes = async (file: File): Promise<{ valid: boolean; error?: string }> => {
    // First do basic validation
    const basicValidation = validateFile(file);
    if (!basicValidation.valid) {
        return basicValidation;
    }

    // Then validate magic bytes
    return validateFileMagicBytes(file);
};

/**
 * Delete a file from Storage (rollback helper).
 * Silently fails if the file doesn't exist.
 */
const rollbackStorageUpload = async (storagePath: string): Promise<void> => {
    try {
        const storage = getStorage();
        const fileRef = storageRef(storage, storagePath);
        await deleteObject(fileRef);
    } catch (error) {
        // Silently ignore - file may not exist or already deleted
        console.warn('Rollback: Could not delete file from storage:', storagePath, error);
    }
};

/**
 * Upload an attachment for a maintenance activity cost
 */
export const uploadMaintenanceActivityAttachment = async (
    buildingKey: string,
    componentKey: string,
    templateId: string,
    file: File,
    uploadedBy: string
): Promise<MaintenanceAttachmentUploadResult> => {
    let storagePath: string | null = null;

    try {
        // Validate file with magic bytes for security
        const validation = await validateFileWithMagicBytes(file);
        if (!validation.valid) {
            return { success: false, error: validation.error };
        }

        const db = getDatabase();
        const storage = getStorage();

        // Generate unique attachment key using Firebase push
        const tempRef = dbRef(db, 'temp');
        const newRef = push(tempRef);
        const attachmentKey = newRef.key!;

        // Storage path for maintenance activity attachments
        storagePath = `maintenance/attachments/${buildingKey}/${componentKey}/${templateId}/${attachmentKey}`;

        // Upload file to Storage
        const fileRef = storageRef(storage, storagePath);
        await uploadBytes(fileRef, file);

        // Get file extension
        const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';

        // Check if image and get dimensions if so
        const isImage = isImageFile(file);

        let attachment: Attachment;

        if (isImage) {
            try {
                const dimensions = await getImageDimensions(file);

                const imageAttachment: ImageAttachment = {
                    key: attachmentKey,
                    author: uploadedBy,
                    date: formatDateForStorage(new Date()),
                    width: dimensions.width,
                    height: dimensions.height,
                    storagePath: storagePath,
                    fileSize: file.size,
                    attachmentType: 'image',
                    fileName: file.name,
                    mimeType: file.type
                };

                attachment = imageAttachment;
            } catch (error) {
                console.warn('Failed to get image dimensions:', error);
                // Fallback: create image attachment with default dimensions
                const imageAttachment: ImageAttachment = {
                    key: attachmentKey,
                    author: uploadedBy,
                    date: formatDateForStorage(new Date()),
                    width: 0,
                    height: 0,
                    storagePath: storagePath,
                    fileSize: file.size,
                    attachmentType: 'image',
                    fileName: file.name,
                    mimeType: file.type
                };
                attachment = imageAttachment;
            }
        } else {
            // For non-image files (PDFs), use FileAttachment
            const fileAttachment: FileAttachment = {
                key: attachmentKey,
                fileName: file.name,
                fileType: fileExtension,
                mimeType: file.type,
                storagePath: storagePath,
                fileSize: file.size,
                uploadedBy: uploadedBy,
                uploadedAt: formatDateOnlyForStorage(new Date()),
                attachmentType: 'file'
            };

            attachment = fileAttachment;
        }

        recordActivity('document.uploaded', { buildingKey, targetKey: componentKey });
        return {
            success: true,
            attachment
        };
    } catch (error) {
        console.error('Error uploading maintenance attachment:', error);

        // Rollback: delete uploaded file if it exists
        if (storagePath) {
            await rollbackStorageUpload(storagePath);
        }

        return {
            success: false,
            error: error instanceof Error ? error.message : 'Ett fel uppstod vid uppladdning'
        };
    }
};

/**
 * Upload an attachment for replacement cost
 */
export const uploadReplacementAttachment = async (
    buildingKey: string,
    componentKey: string,
    file: File,
    uploadedBy: string
): Promise<MaintenanceAttachmentUploadResult> => {
    let storagePath: string | null = null;

    try {
        // Validate file with magic bytes for security
        const validation = await validateFileWithMagicBytes(file);
        if (!validation.valid) {
            return { success: false, error: validation.error };
        }

        const db = getDatabase();
        const storage = getStorage();

        // Generate unique attachment key using Firebase push
        const tempRef = dbRef(db, 'temp');
        const newRef = push(tempRef);
        const attachmentKey = newRef.key!;

        // Storage path for replacement attachments
        storagePath = `maintenance/attachments/${buildingKey}/${componentKey}/replacement/${attachmentKey}`;

        // Upload file to Storage
        const fileRef = storageRef(storage, storagePath);
        await uploadBytes(fileRef, file);

        // Get file extension
        const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';

        // Check if image and get dimensions if so
        const isImage = isImageFile(file);

        let attachment: Attachment;

        if (isImage) {
            try {
                const dimensions = await getImageDimensions(file);

                const imageAttachment: ImageAttachment = {
                    key: attachmentKey,
                    author: uploadedBy,
                    date: formatDateForStorage(new Date()),
                    width: dimensions.width,
                    height: dimensions.height,
                    storagePath: storagePath,
                    fileSize: file.size,
                    attachmentType: 'image',
                    fileName: file.name,
                    mimeType: file.type
                };

                attachment = imageAttachment;
            } catch (error) {
                console.warn('Failed to get image dimensions:', error);
                const imageAttachment: ImageAttachment = {
                    key: attachmentKey,
                    author: uploadedBy,
                    date: formatDateForStorage(new Date()),
                    width: 0,
                    height: 0,
                    storagePath: storagePath,
                    fileSize: file.size,
                    attachmentType: 'image',
                    fileName: file.name,
                    mimeType: file.type
                };
                attachment = imageAttachment;
            }
        } else {
            const fileAttachment: FileAttachment = {
                key: attachmentKey,
                fileName: file.name,
                fileType: fileExtension,
                mimeType: file.type,
                storagePath: storagePath,
                fileSize: file.size,
                uploadedBy: uploadedBy,
                uploadedAt: formatDateOnlyForStorage(new Date()),
                attachmentType: 'file'
            };

            attachment = fileAttachment;
        }

        recordActivity('document.uploaded', { buildingKey, targetKey: componentKey });
        return {
            success: true,
            attachment
        };
    } catch (error) {
        console.error('Error uploading replacement attachment:', error);

        // Rollback: delete uploaded file if it exists
        if (storagePath) {
            await rollbackStorageUpload(storagePath);
        }

        return {
            success: false,
            error: error instanceof Error ? error.message : 'Ett fel uppstod vid uppladdning'
        };
    }
};

/**
 * Get download URL for an attachment
 */
export const getMaintenanceAttachmentDownloadUrl = async (
    storagePath: string
): Promise<string> => {
    try {
        const storage = getStorage();
        const fileRef = storageRef(storage, storagePath);
        return await getDownloadURL(fileRef);
    } catch (error) {
        console.error('Error getting attachment download URL:', error);
        throw error;
    }
};
